import type { Feature, FeatureCollection, Geometry, Position } from 'geojson';
import type { WhispRiskPart, WhispSiteView } from '../../../model';

export type WhispFeatureMark = 'risk' | 'ok';

export interface WhispFeatureFinding {
  mark: WhispFeatureMark;
  groupIds: number[];
  parts: WhispRiskPart[];
}

export type WhispFeatureLookup = (feature: Feature) => WhispFeatureFinding | undefined;

const SETTLED_WITH_RESULT = new Set(['OK', 'RISK']);
const COORDINATE_PRECISION = 1e7;

export function whispFeatureLookup(
  site: FeatureCollection | undefined,
  view: WhispSiteView | null | undefined
): WhispFeatureLookup | null {
  if (!site || !view || !SETTLED_WITH_RESULT.has(view.status)) return null;
  const findings = new Map<string, WhispFeatureFinding>();
  site.features.forEach((feature, sourceFeature) =>
    checkedGeometries(feature.geometry).forEach((geometry, sourcePart) => {
      const parts = (view.riskParts ?? []).filter(
        (part) => part.sourceFeature === sourceFeature && (part.sourcePart ?? 0) === sourcePart
      );
      findings.set(geometryKey(geometry), {
        mark: parts.length ? 'risk' : 'ok',
        groupIds: groupIdsOf(view, parts),
        parts,
      });
    })
  );
  return (feature) => (feature.geometry ? findings.get(geometryKey(feature.geometry)) : undefined);
}

function checkedGeometries(geometry: Geometry | null): Geometry[] {
  if (geometry?.type === 'Polygon' || geometry?.type === 'Point') return [geometry];
  if (geometry?.type === 'MultiPolygon')
    return geometry.coordinates.map((coordinates) => ({ type: 'Polygon', coordinates }));
  return [];
}

function groupIdsOf(view: WhispSiteView, parts: WhispRiskPart[]): number[] {
  const partIds = new Set(parts.map((part) => part.partId));
  return (view.riskGroups ?? [])
    .filter((group) => group.riskPartIds.some((partId) => partIds.has(partId)))
    .map((group) => group.groupId);
}

function geometryKey(geometry: Geometry): string {
  if (geometry.type === 'Point') return `point:${positionKey(geometry.coordinates)}`;
  if (geometry.type !== 'Polygon') return `other:${geometry.type}`;
  const ring = geometry.coordinates[0] ?? [];
  const open = ring.length > 1 ? ring.slice(0, -1) : ring;
  return `polygon:${open.map(positionKey).sort().join(';')}`;
}

function positionKey([lng, lat]: Position): string {
  return `${Math.round(lng * COORDINATE_PRECISION)},${Math.round(lat * COORDINATE_PRECISION)}`;
}
